import { css } from '@emotion/react'
import { Cell, Grid, Heading, HFlow, Icon, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import AccordionList from 'components/accordion/AccordionList'
import { useSession } from 'components/auth/useSession'
import { confirm } from 'components/modals/confirm'
import { ButtonLink } from 'components/route'
import { useFlags } from 'config/useFlagsContext'
import { isAfter } from 'date-fns'
import { useHistoricoPrescricaoQuery } from 'graphql/hooks.generated'
import {
  AtendimentoProfissional,
  HistoricoPrescricaoQueryInput,
  ReceitaMedicamento,
  TipoReceitaEnum,
  UnidadeMedidaTempoEnum,
} from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import { noop } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouteMatch } from 'react-router'
import { useHistory } from 'react-router-dom'
import { emptyArrayOf } from 'util/array'
import { isEmpty } from 'util/checks'
import { formatDate } from 'util/date/formatDate'
import { getEditAtendProfId } from 'view/atendimentos/atendimento-individual/util-atendimentoIndividual'
import { TipoReceitaTag } from 'view/atendimentos/detail/components/TipoReceitaTag'
import { formatCidOrCiap } from 'view/atendimentos/utils/format-atendimento'

import { grupoCboPrescricao } from '../../acessos'
import { periodoTratamento } from '../components/PeriodoTratamentoSelectField'
import { ADICIONAR_PRESCRICAO_SUB_PATH, MedicamentoFormModel } from '../model-prescricao'
import { convertReceitaMedicamentoToMedicamento } from '../utils/converter-prescricao'
import HistoricoPrescricaoTableFilter from './HistoricoPrescricaoTableFilter'

export interface HistoricoPrescricaoTableProps {
  prontuarioId: ID
  atendimentoProfissionalId?: ID
  isAtendimentoProcedimento: boolean
  isAtendimentoObservacao?: boolean
  setMedicamentosToReplicate?(medicamentos: MedicamentoFormModel[]): void
  dataReferencia: Instant
  readOnly?: boolean
  usoContinuoGrupoIdsInterrompidos?: ReadonlySet<ID>
}

function getTooltipRenovarText(
  tratamentoNaoIniciado: boolean,
  isMedicamentoAtivo: boolean,
  isInterrompido: boolean
): string {
  if (tratamentoNaoIniciado) {
    return 'Não é possível renovar a prescrição de um tratamento ainda não iniciado'
  }
  if (isInterrompido) {
    return 'Não é possível renovar prescrições para tratamentos interrompidos'
  }
  if (isMedicamentoAtivo) {
    return 'Renovar prescrição'
  }
  return 'Este medicamento não está mais presente na base de dados'
}

export default function HistoricoPrescricaoTable(props: HistoricoPrescricaoTableProps) {
  const {
    prontuarioId,
    isAtendimentoProcedimento,
    atendimentoProfissionalId,
    isAtendimentoObservacao = false,
    setMedicamentosToReplicate = noop,
    dataReferencia,
    readOnly,
    usoContinuoGrupoIdsInterrompidos = new Set(emptyArrayOf<ID>()),
  } = props
  const { analytics } = useFirebase()
  const { getServerTimeNow } = useServerTime()
  const history = useHistory()
  const match = useRouteMatch()
  const addPrescriptionPath = useMemo(() => `${match.url}${ADICIONAR_PRESCRICAO_SUB_PATH}`, [match.url])
  const { hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })
  const { MOTIVO_PRESCRICAO_ENABLED } = useFlags()
  const dataInicioAtendimento = dataReferencia

  const podeReplicar = !isAtendimentoProcedimento && hasCboAuth(grupoCboPrescricao.adicionar)

  const editAtendProfId = getEditAtendProfId(isAtendimentoObservacao, atendimentoProfissionalId)

  const [currentFilter, setCurrentFilter] = useState<HistoricoPrescricaoQueryInput>({
    prontuarioId,
    editAtendProfId,
    pageParams: {
      size: 5,
    },
  })

  useEffect(() => {
    setCurrentFilter((prevCurrentFilter) => ({ ...prevCurrentFilter, editAtendProfId }))
  }, [editAtendProfId])

  const { data, loading } = useHistoricoPrescricaoQuery({
    variables: { input: currentFilter },
  })
  const historico = data?.historicoPrescricao

  const handleOnClickReplicarMedicamento = useCallback(
    (receitaMedicamento: ReceitaMedicamento, isVersaoAnterior: boolean, isRenovacao = false) => {
      const medicamento: MedicamentoFormModel = convertReceitaMedicamentoToMedicamento(
        receitaMedicamento,
        isVersaoAnterior,
        true,
        dataInicioAtendimento,
        isRenovacao
      )
      setMedicamentosToReplicate([medicamento])
    },
    [dataInicioAtendimento, setMedicamentosToReplicate]
  )

  const handleOnClickReplicar = useCallback(
    (receitaMedicamento: ReceitaMedicamento[], isVersaoAnterior: boolean, isRenovacao = false) => {
      const medicamentos: MedicamentoFormModel[] = receitaMedicamento.map((item) =>
        convertReceitaMedicamentoToMedicamento(item, isVersaoAnterior, true, dataInicioAtendimento, isRenovacao)
      )
      medicamentos.sort((a, b) => {
        return (a.registroManual ? a.principioAtivoText : a.principioAtivoCombo.principioAtivo.nome).localeCompare(
          b.registroManual ? b.principioAtivoText : b.principioAtivoCombo.principioAtivo.nome
        )
      })
      setMedicamentosToReplicate(medicamentos)
    },
    [dataInicioAtendimento, setMedicamentosToReplicate]
  )

  const handleOnConfirmReplicate = useCallback(
    (receitaMedicamento: ReceitaMedicamento[], isReplicarPrescricaoInteira: boolean, isRenovacao = false) => {
      const titleText = isRenovacao ? 'renovar' : 'replicar'
      const bodyText = isRenovacao ? 'renovação' : 'replicação'
      confirm({
        type: 'primary',
        title: `Deseja ${titleText} ${isReplicarPrescricaoInteira ? 'esta prescrição' : 'este medicamento'}?`,
        body: `Este medicamento foi prescrito em versão anterior do sistema. Para concluir sua ${bodyText} será necessário completar informações. Deseja continuar?`,
        onConfirm: () => {
          analytics.logEvent(`replicar_${isReplicarPrescricaoInteira ? 'prescricao' : 'medicamento'}_versao_anterior`)
          isReplicarPrescricaoInteira
            ? handleOnClickReplicar(receitaMedicamento, true, isRenovacao)
            : handleOnClickReplicarMedicamento(receitaMedicamento[0], true, isRenovacao)
        },
        onCancel: () => history.push(match.url.replace(ADICIONAR_PRESCRICAO_SUB_PATH, '')),
        confirmLabel: 'Continuar',
        cancelLabel: 'Cancelar',
        depthLevel: 2,
        manageOverflow: true,
      })()
    },
    [analytics, handleOnClickReplicar, handleOnClickReplicarMedicamento, history, match.url]
  )

  const medicamentoInativo = (receita: ReceitaMedicamento) =>
    !receita.medicamento.medicamentoCatmat?.ativo && !receita.registroManual

  const showRenovacaoButton = (receita: ReceitaMedicamento) => receita.usoContinuo && !receita.tratamentoInterrompido

  const renderItemHeader = (item: AtendimentoProfissional) => {
    const prof = item.lotacao.profissional

    let subtitle: string = `${formatDate(item.finalizadoEm)} | ${item.lotacao.profissional.nome}`
    if (prof.conselhoClasse && prof.ufEmissoraConselhoClasse && prof.ufEmissoraConselhoClasse) {
      subtitle = subtitle.concat(
        ` - ${prof.conselhoClasse.sigla} ${prof.ufEmissoraConselhoClasse.sigla} ${prof.numeroConselhoClasse}`
      )
    }

    return (
      <HFlow
        style={css`
          width: 100%;
        `}
      >
        <VFlow vSpacing={0}>
          <Heading level={5}>
            {item.receitaMedicamento
              .map(
                (receitaMedicamento) =>
                  `${receitaMedicamento.medicamento.principioAtivo} ${
                    receitaMedicamento.medicamento.concentracao ?? ''
                  }`
              )
              .join(' | ')}
          </Heading>
          <Text>{subtitle}</Text>
        </VFlow>
      </HFlow>
    )
  }

  const renderItemBody = (item: AtendimentoProfissional) => {
    return item.receitaMedicamento?.map((receita) => {
      const prescricaoInicial = receita.receitaUsoContinuoGrupo ?? receita
      const tratamentoNaoIniciado = isAfter(prescricaoInicial.dtInicioTratamento, getServerTimeNow())
      const idParaVerificar = receita.receitaUsoContinuoGrupo?.id ?? receita.id
      const isInterrompido = usoContinuoGrupoIdsInterrompidos.has(idParaVerificar)
      const tooltipRenovarText = getTooltipRenovarText(
        tratamentoNaoIniciado,
        !medicamentoInativo(receita),
        isInterrompido
      )

      return (
        <Grid
          key={receita.id}
          style={css`
            padding: 0.5rem 0.5rem 0.5rem 1rem;
            margin: auto;
            clip-path: margin-box;
          `}
        >
          <Cell size={10}>
            <Grid key={receita.id}>
              <Cell size={8}>
                <HFlow alignItems='center' hSpacing={0.5}>
                  <Heading
                    level={5}
                    style={css`
                      word-break: break-word;
                    `}
                  >
                    {`${receita.medicamento.principioAtivo} ${receita.medicamento.concentracao ?? ''}`}
                  </Heading>
                  {receita.tratamentoInterrompido && <Tag type='normal'>Tratamento interrompido</Tag>}
                  {receita.usoContinuo && <Tag type='normal'>Uso contínuo</Tag>}
                  {receita.tipoReceita !== TipoReceitaEnum.COMUM && (
                    <TipoReceitaTag tipoReceita={receita.tipoReceita} />
                  )}
                </HFlow>
                <Text>
                  {[receita.posologia, receita.aplicacaoMedicamento.nome, receita.medicamento.formaFarmaceutica?.nome]
                    .filterNotNull()
                    .join(' | ')
                    .trim()}
                </Text>
                {receita.tratamentoInterrompido && (
                  <VFlow vSpacing={0}>
                    <Text fontWeight='bold'>Interrupção</Text>
                    {receita.dtInterrupcao && <Text>{`${formatDate(receita.dtInterrupcao)}`}</Text>}
                    {receita.justificativaInterrupcao && <Text>{receita.justificativaInterrupcao}</Text>}
                  </VFlow>
                )}
              </Cell>
              <Cell size={4}>
                <Heading level={5}>
                  {`Período: ${formatDate(receita.dtInicioTratamento)}
                 - ${
                   item?.versaoAnterior
                     ? ` ${periodoTratamento[receita.medidaTempoTratamento]}`
                     : receita.medidaTempoTratamento !== UnidadeMedidaTempoEnum.INDETERMINADO
                     ? formatDate(receita.dtFimTratamento)
                     : `${periodoTratamento[receita.medidaTempoTratamento]}`
                 }
            `}
                </Heading>
              </Cell>
              <Cell size={12}>
                <Heading level={5}>Recomendações</Heading>
                {receita.recomendacoes ? (
                  <Text
                    style={css`
                      white-space: pre-wrap;
                      overflow-wrap: anywhere;
                    `}
                  >
                    {receita.recomendacoes}
                  </Text>
                ) : (
                  <Text fontStyle='italic'>Não há recomendações para o medicamento</Text>
                )}
              </Cell>
              {MOTIVO_PRESCRICAO_ENABLED && (
                <Cell size={12}>
                  <Heading level={5}>Motivo da prescrição</Heading>
                  <Text>{formatCidOrCiap(receita.cid10, receita.ciap)}</Text>
                </Cell>
              )}
            </Grid>
          </Cell>
          <Cell
            size={2}
            alignSelf='center'
            style={css`
              text-align: end;
            `}
          >
            {!readOnly && podeReplicar && (
              <HFlow justifyContent='flex-end' hSpacing={0}>
                {showRenovacaoButton(receita) && (
                  <Tooltip text={tooltipRenovarText}>
                    <ButtonLink
                      size='small'
                      skin='ghost'
                      kind='normal'
                      to={addPrescriptionPath}
                      disabled={medicamentoInativo(receita) || tratamentoNaoIniciado || isInterrompido}
                      onClick={() => {
                        analytics.logEvent('click_icon_renovar_prescricao')
                        item.versaoAnterior
                          ? handleOnConfirmReplicate([receita], false, true)
                          : handleOnClickReplicarMedicamento(receita, false, true)
                      }}
                    >
                      <Icon icon='sync' />
                    </ButtonLink>
                  </Tooltip>
                )}
                <Tooltip
                  text={
                    !medicamentoInativo(receita)
                      ? 'Replicar medicamento'
                      : 'Este medicamento não está mais presente na base de dados'
                  }
                >
                  <ButtonLink
                    size='small'
                    skin='ghost'
                    kind='normal'
                    disabled={medicamentoInativo(receita)}
                    to={addPrescriptionPath}
                    onClick={() => {
                      analytics.logEvent('click_button_replicar_medicamento')
                      item.versaoAnterior
                        ? handleOnConfirmReplicate([receita], false, false)
                        : handleOnClickReplicarMedicamento(receita, false, false)
                    }}
                    data-cy='ReplicarMedicamentoButton'
                  >
                    <Icon icon='copyOutline' />
                  </ButtonLink>
                </Tooltip>
              </HFlow>
            )}
          </Cell>
        </Grid>
      )
    })
  }

  const renderHeaderButtons = (item: AtendimentoProfissional) => {
    if (readOnly || !podeReplicar) {
      return null
    }

    const prescricaoPrincipal = item.receitaMedicamento.first()
    const showRenovacaoHeaderButton = item.receitaMedicamento.length === 1 && showRenovacaoButton(prescricaoPrincipal)

    const prescricaoInicial = prescricaoPrincipal.receitaUsoContinuoGrupo ?? prescricaoPrincipal
    const tratamentoNaoIniciado = isAfter(prescricaoInicial.dtInicioTratamento, getServerTimeNow())

    const medicamentosAtivos = item.receitaMedicamento.filter((receita) => !medicamentoInativo(receita))
    const disableButtons = medicamentosAtivos.length === 0

    const idParaVerificar = prescricaoPrincipal.receitaUsoContinuoGrupo?.id ?? prescricaoPrincipal.id
    const isInterrompido = usoContinuoGrupoIdsInterrompidos.has(idParaVerificar)
    const tooltipRenovarText = getTooltipRenovarText(tratamentoNaoIniciado, !disableButtons, isInterrompido)

    return (
      <HFlow
        alignItems='center'
        style={css`
          left: 1;
        `}
        hSpacing={0}
      >
        {showRenovacaoHeaderButton && (
          <Tooltip text={tooltipRenovarText}>
            <ButtonLink
              size='small'
              skin='ghost'
              kind='normal'
              disabled={disableButtons || tratamentoNaoIniciado || isInterrompido}
              to={addPrescriptionPath}
              onClick={() => {
                analytics.logEvent('click_icon_renovar_prescricao')
                item.versaoAnterior
                  ? handleOnConfirmReplicate(medicamentosAtivos, true, true)
                  : handleOnClickReplicar(medicamentosAtivos, false, true)
              }}
            >
              <Icon icon='sync' />
            </ButtonLink>
          </Tooltip>
        )}
        <Tooltip
          text={
            !disableButtons ? 'Replicar prescrição' : 'Estes medicamentos não estão mais presentes na base de dados'
          }
        >
          <ButtonLink
            size='small'
            skin='ghost'
            kind='normal'
            disabled={disableButtons}
            to={addPrescriptionPath}
            onClick={() => {
              analytics.logEvent('click_button_replicar_prescricao')
              item.versaoAnterior
                ? handleOnConfirmReplicate(medicamentosAtivos, true, false)
                : handleOnClickReplicar(medicamentosAtivos, item.versaoAnterior, false)
            }}
            data-cy='ReplicarPrescricaoButton'
          >
            <Icon icon='copyOutline' />
          </ButtonLink>
        </Tooltip>
      </HFlow>
    )
  }

  const noResultPlaceholder =
    !currentFilter?.query && (!historico || isEmpty(historico.content))
      ? 'Nenhuma prescrição realizada'
      : 'Nenhum registro encontrado'

  return (
    <AccordionList<AtendimentoProfissional>
      header={<HistoricoPrescricaoTableFilter initialValues={currentFilter} onChange={setCurrentFilter} />}
      loading={loading}
      data={historico}
      onChange={setCurrentFilter}
      accordionItemHeader={renderItemHeader}
      accordionItemBody={renderItemBody}
      accordionItemButtons={renderHeaderButtons}
      noResultPlaceholder={noResultPlaceholder}
    />
  )
}
